import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt, FaLock, FaUserShield, FaTrash, FaEye, FaEyeSlash, FaBan, FaChevronRight, FaTimes, FaExclamationTriangle, FaCheck, FaPauseCircle, FaMoon, FaFileContract } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { securityAPI } from '../utils/api';
import { toast } from 'sonner';

const ToggleSwitch = ({ checked, onChange, testId }) => (
  <button
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#4A90E2]' : 'bg-gray-300'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

const Settings = () => {
  const navigate = useNavigate();
  const [privacy, setPrivacy] = useState({
    is_private: false,
    dm_privacy: 'everyone',
    show_online_status: true,
    show_last_seen: true,
    show_activity: true,
    show_read_receipts: true,
  });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [suspendPassword, setSuspendPassword] = useState('');
  const [suspendDuration, setSuspendDuration] = useState('indefinite');
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  useEffect(() => {
    loadPrivacy();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', darkMode);
    securityAPI.updatePreferences({ dark_mode: darkMode }).catch(() => {});
  }, [darkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrivacy = async () => {
    try {
      const res = await securityAPI.getPrivacy();
      setPrivacy(res.data);
    } catch (err) { console.error('Failed to load privacy', err); }
  };

  const updatePrivacy = async (key, value) => {
    const prev = { ...privacy };
    setPrivacy(p => ({ ...p, [key]: value }));
    try {
      await securityAPI.updatePrivacy({ [key]: value });
      toast.success('Setting updated');
    } catch (err) {
      setPrivacy(prev);
      toast.error('Failed to update');
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await securityAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed');
      setShowPasswordModal(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await securityAPI.deleteAccount({ password: deletePassword });
      sessionStorage.clear();
      toast.success('Account deleted');
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete account');
    } finally { setLoading(false); }
  };

  const handleSuspendAccount = async () => {
    setLoading(true);
    try {
      await securityAPI.suspendAccount({ password: suspendPassword, duration: suspendDuration });
      sessionStorage.clear();
      toast.success('Account suspended. Log back in anytime to reactivate.');
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to suspend account');
    } finally { setLoading(false); }
  };

  const loadBlocked = async () => {
    try {
      const res = await securityAPI.getBlocked();
      setBlockedUsers(res.data);
    } catch (err) { console.error('Failed to load blocked users', err); }
  };

  const handleUnblock = async (userId) => {
    try {
      await securityAPI.unblockUser(userId);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User unblocked');
    } catch (err) { toast.error('Failed to unblock'); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10" data-testid="settings-page">
      <h1 className="text-2xl font-bold mb-6">Security & Privacy</h1>

      {/* Account Privacy */}
      <section className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center space-x-2">
            <FaUserShield className="text-[#4A90E2]" />
            <h2 className="font-bold text-lg">Account Privacy</h2>
          </div>
        </div>

        <div className="divide-y divide-gray-50">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Private Account</p>
              <p className="text-xs text-gray-500 mt-0.5">Only followers can see your posts</p>
            </div>
            <ToggleSwitch checked={privacy.is_private} onChange={v => updatePrivacy('is_private', v)} testId="toggle-private-account" />
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Who can message you</p>
              <p className="text-xs text-gray-500 mt-0.5">Control who can send you DMs</p>
            </div>
            <select
              data-testid="dm-privacy-select"
              value={privacy.dm_privacy}
              onChange={e => updatePrivacy('dm_privacy', e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/30"
            >
              <option value="everyone">Everyone</option>
              <option value="followers">Followers only</option>
              <option value="nobody">Nobody</option>
            </select>
          </div>
        </div>
      </section>

      {/* Activity Visibility */}
      <section className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center space-x-2">
            <FaEye className="text-[#4A90E2]" />
            <h2 className="font-bold text-lg">Activity Visibility</h2>
          </div>
        </div>

        <div className="divide-y divide-gray-50">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Show Online Status</p>
              <p className="text-xs text-gray-500 mt-0.5">Others can see when you&apos;re online</p>
            </div>
            <ToggleSwitch checked={privacy.show_online_status} onChange={v => updatePrivacy('show_online_status', v)} testId="toggle-online-status" />
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Show Last Seen</p>
              <p className="text-xs text-gray-500 mt-0.5">Others can see when you were last active</p>
            </div>
            <ToggleSwitch checked={privacy.show_last_seen} onChange={v => updatePrivacy('show_last_seen', v)} testId="toggle-last-seen" />
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Show Activity</p>
              <p className="text-xs text-gray-500 mt-0.5">Others can see your likes, comments, and activity</p>
            </div>
            <ToggleSwitch checked={privacy.show_activity} onChange={v => updatePrivacy('show_activity', v)} testId="toggle-activity" />
          </div>

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="font-medium text-sm">Read Receipts</p>
              <p className="text-xs text-gray-500 mt-0.5">Let others know when you&apos;ve read their messages</p>
            </div>
            <ToggleSwitch checked={privacy.show_read_receipts} onChange={v => updatePrivacy('show_read_receipts', v)} testId="toggle-read-receipts" />
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center space-x-2">
            <FaLock className="text-[#4A90E2]" />
            <h2 className="font-bold text-lg">Security</h2>
          </div>
        </div>

        <div className="divide-y divide-gray-50">
          <button
            data-testid="change-password-btn"
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center justify-between w-full px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="text-left">
              <p className="font-medium text-sm">Change Password</p>
              <p className="text-xs text-gray-500 mt-0.5">Update your account password</p>
            </div>
            <FaChevronRight className="text-gray-300" size={12} />
          </button>

          <button
            data-testid="blocked-users-btn"
            onClick={() => { setShowBlockedModal(true); loadBlocked(); }}
            className="flex items-center justify-between w-full px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="text-left">
              <p className="font-medium text-sm">Blocked Users</p>
              <p className="text-xs text-gray-500 mt-0.5">Manage blocked accounts</p>
            </div>
            <FaChevronRight className="text-gray-300" size={12} />
          </button>
        </div>
      </section>

      {/* Appearance */}
      <section className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center space-x-2">
            <FaMoon className="text-[#4A90E2]" />
            <h2 className="font-bold text-lg">Appearance</h2>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">Dark Mode</p>
            <p className="text-xs text-gray-500 mt-0.5">Switch to a darker color scheme</p>
          </div>
          <ToggleSwitch checked={darkMode} onChange={setDarkMode} testId="dark-mode-toggle" />
        </div>
      </section>

      {/* Legal */}
      <section className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center space-x-2">
            <FaFileContract className="text-[#4A90E2]" />
            <h2 className="font-bold text-lg">Legal</h2>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          <Link
            to="/privacy"
            data-testid="settings-privacy-link"
            className="flex items-center justify-between w-full px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="text-left">
              <p className="font-medium text-sm">Privacy Policy</p>
              <p className="text-xs text-gray-500 mt-0.5">How we collect and use your data</p>
            </div>
            <FaChevronRight className="text-gray-300" size={12} />
          </Link>
          <Link
            to="/terms"
            data-testid="settings-terms-link"
            className="flex items-center justify-between w-full px-5 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="text-left">
              <p className="font-medium text-sm">Terms of Use</p>
              <p className="text-xs text-gray-500 mt-0.5">Rules and guidelines for using TalkinPoint</p>
            </div>
            <FaChevronRight className="text-gray-300" size={12} />
          </Link>
        </div>
      </section>

      {/* Danger Zone */}
      <section className="bg-white rounded-2xl border border-red-100 mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-red-50">
          <div className="flex items-center space-x-2">
            <FaExclamationTriangle className="text-red-500" />
            <h2 className="font-bold text-lg text-red-600">Danger Zone</h2>
          </div>
        </div>

        <button
          data-testid="suspend-account-btn"
          onClick={() => setShowSuspendModal(true)}
          className="flex items-center justify-between w-full px-5 py-4 hover:bg-amber-50 transition-colors border-b border-gray-50"
        >
          <div className="text-left">
            <p className="font-medium text-sm text-amber-600">Suspend Account</p>
            <p className="text-xs text-gray-500 mt-0.5">Take a break. Your data stays safe until you return.</p>
          </div>
          <FaPauseCircle className="text-amber-400" size={14} />
        </button>

        <button
          data-testid="delete-account-btn"
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center justify-between w-full px-5 py-4 hover:bg-red-50 transition-colors"
        >
          <div className="text-left">
            <p className="font-medium text-sm text-red-600">Delete Account</p>
            <p className="text-xs text-gray-500 mt-0.5">Permanently delete your account and all data</p>
          </div>
          <FaTrash className="text-red-400" size={14} />
        </button>
      </section>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="change-password-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="space-y-3">
              <input type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/30" data-testid="current-password-input" />
              <input type="password" placeholder="New password (8+ chars, upper, lower, number)" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/30" data-testid="new-password-input" />
              <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/30" data-testid="confirm-password-input" />
            </div>
            <button disabled={loading || !currentPassword || !newPassword || !confirmPassword} onClick={handleChangePassword}
              data-testid="submit-change-password"
              className="w-full mt-4 bg-[#4A90E2] text-white font-medium py-3 rounded-xl hover:bg-[#3a7bd5] transition-colors disabled:opacity-50">
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}

      {/* Blocked Users Modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="blocked-users-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold">Blocked Users</h3>
              <button onClick={() => setShowBlockedModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            {blockedUsers.length === 0 ? (
              <p className="text-sm text-center py-8">No blocked users</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {blockedUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50">
                    <Link to={`/profile/${user.username}`} className="flex items-center space-x-3">
                      <img src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name}`} alt="" className="w-9 h-9 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" />
                      <div>
                        <p className="font-medium text-sm hover:underline">{user.full_name}</p>
                        <p className="text-xs text-gray-500">@{user.username}</p>
                      </div>
                    </Link>
                    <button onClick={() => handleUnblock(user.id)}
                      data-testid={`unblock-${user.id}`}
                      className="text-xs font-medium text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors">
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suspend Account Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="suspend-account-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-amber-600">Suspend Account</h3>
              <button onClick={() => setShowSuspendModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-700 font-medium">Your account will be hidden from others.</p>
              <p className="text-xs text-amber-600 mt-1">Your profile, posts, and activity will be invisible. Log back in anytime to reactivate.</p>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium mb-2">How long?</p>
              <div className="space-y-2">
                {[
                  { value: '1week', label: '1 Week' },
                  { value: '1month', label: '1 Month' },
                  { value: 'indefinite', label: 'Until I come back' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSuspendDuration(opt.value)}
                    data-testid={`suspend-${opt.value}`}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border transition-all ${
                      suspendDuration === opt.value
                        ? 'border-amber-400 bg-amber-50 text-amber-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <input type="password" placeholder="Enter your password to confirm" value={suspendPassword} onChange={e => setSuspendPassword(e.target.value)}
              className="w-full border border-amber-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="suspend-password-input" />
            <button disabled={loading || !suspendPassword} onClick={handleSuspendAccount}
              data-testid="confirm-suspend-account"
              className="w-full mt-4 bg-amber-500 text-white font-medium py-3 rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50">
              {loading ? 'Suspending...' : 'Suspend My Account'}
            </button>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="delete-account-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-red-600">Delete Account</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="bg-red-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-700 font-medium">This action is permanent and cannot be undone.</p>
              <p className="text-xs text-red-600 mt-1">All your posts, messages, followers, and data will be permanently deleted.</p>
            </div>
            <input type="password" placeholder="Enter your password to confirm" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
              className="w-full border border-red-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-300" data-testid="delete-password-input" />
            <button disabled={loading || !deletePassword} onClick={handleDeleteAccount}
              data-testid="confirm-delete-account"
              className="w-full mt-4 bg-red-500 text-white font-medium py-3 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50">
              {loading ? 'Deleting...' : 'Delete My Account'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
